import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, TrendingUp, Eye, HelpCircle } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL;

function headers() {
  var t = localStorage.getItem('admin_token');
  return t ? { Authorization: 'Bearer ' + t } : {};
}

var RANGES = [
  { id: 30, label: 'Last 30 days' },
  { id: 90, label: 'Last 90 days' },
  { id: 0, label: 'All time' }
];

function money(n) {
  return '$' + (Number(n) || 0).toFixed(2);
}

function Bar(props) {
  return (
    <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden mt-2">
      <div className="h-full bg-[#d4af37] rounded-full transition-all"
           style={{ width: Math.max(2, Math.min(100, props.pct)) + '%' }} />
    </div>
  );
}

function Table(props) {
  var rows = props.rows || [];
  if (!rows.length) {
    return <p className="text-neutral-600 text-sm py-6">{props.empty}</p>;
  }
  return (
    <div className="space-y-3" data-testid={props.testid}>
      {rows.map(function(r) {
        var count = props.countKey ? r[props.countKey] : r.orders;
        var revenue = props.revenueKey ? r[props.revenueKey] : r.revenue;
        return (
          <div key={r.source}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-neutral-200 text-sm truncate">{r.label}</span>
              <span className="text-neutral-400 text-xs shrink-0">
                <span className="text-white font-semibold">{count}</span> · {r.share}% · <span className="text-[#d4af37]">{money(revenue)}</span>
              </span>
            </div>
            <Bar pct={r.share} />
          </div>
        );
      })}
    </div>
  );
}

// "Where they come from": what customers told us, plus what the browser told us.
export default function SourcesPanel() {
  var [days, setDays] = useState(30);
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState('');

  var load = useCallback(async function() {
    setLoading(true); setErr('');
    try {
      var r = await fetch(API + '/api/admin/attribution/stats?days=' + days, { headers: headers() });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not load the numbers (HTTP ' + r.status + ')');
      setData(d);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(function() { load(); }, [load]);

  var past = (data && data.past_customers) || { answered: 0, rows: [] };

  return (
    <div className="space-y-5" data-testid="sources-panel">
      <div className="bg-neutral-900 border border-white/10 rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <h3 className="text-white font-semibold mr-2">Where they come from</h3>
          {RANGES.map(function(r) {
            var on = days === r.id;
            return (
              <button key={r.id} onClick={function() { setDays(r.id); }}
                      data-testid={'sources-range-' + r.id}
                      className={'text-xs rounded-full px-3.5 py-1.5 border transition-colors ' +
                        (on ? 'bg-[#d4af37] text-black border-[#d4af37] font-semibold'
                            : 'text-neutral-300 border-white/15 hover:border-white/40')}>
                {r.label}
              </button>
            );
          })}
          <button onClick={load} data-testid="sources-refresh"
                  className="ml-auto flex items-center gap-1.5 text-xs text-neutral-300 border border-white/15 hover:border-white/40 rounded-full px-3 py-1.5">
            <RefreshCw className={'w-3.5 h-3.5' + (loading ? ' animate-spin' : '')} /> Refresh
          </button>
        </div>

        {loading ? (
          <p className="py-10 text-center text-neutral-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
          </p>
        ) : err ? (
          <p className="text-red-300 text-sm py-6" data-testid="sources-error">{err}</p>
        ) : data ? (
          <div className="space-y-7">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="bg-black border border-white/10 rounded-xl p-4">
                <p className="text-neutral-500 text-xs">Orders in this period</p>
                <p className="text-white text-2xl font-bold mt-1" data-testid="sources-orders">{data.orders_in_period}</p>
              </div>
              <div className="bg-black border border-white/10 rounded-xl p-4">
                <p className="text-neutral-500 text-xs">Answered the question</p>
                <p className="text-white text-2xl font-bold mt-1" data-testid="sources-answered">
                  {data.answered} <span className="text-neutral-500 text-sm font-normal">({data.answer_rate}%)</span>
                </p>
              </div>
              <div className="bg-black border border-white/10 rounded-xl p-4">
                <p className="text-neutral-500 text-xs">Tracked silently</p>
                <p className="text-white text-2xl font-bold mt-1" data-testid="sources-auto-count">{data.tracked_automatically}</p>
              </div>
            </div>

            <div>
              <p className="text-[#d4af37] text-[11px] uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5" /> What customers told you
              </p>
              <Table rows={data.asked} testid="sources-asked"
                     empty="Nobody has answered yet — the question appears on the thank-you page after every purchase." />
            </div>

            <div>
              <p className="text-neutral-400 text-[11px] uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                <Eye className="w-3.5 h-3.5" /> What the browser told us (no question asked)
              </p>
              <Table rows={data.auto} testid="sources-auto"
                     empty="No visits tracked yet in this period." />
            </div>

            <div>
              <p className="text-neutral-400 text-[11px] uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                <HelpCircle className="w-3.5 h-3.5" /> Past customers who answered by email
              </p>
              <Table rows={past.rows} countKey="customers" revenueKey="lifetime_revenue"
                     testid="sources-past"
                     empty="Nothing yet. Past customers get the same one-tap question inside the announcement emails — answers land here." />
              {past.answered ? (
                <p className="text-neutral-600 text-xs mt-3">
                  {past.answered} past customer(s) answered. Revenue here is their lifetime spend, so it is
                  listed separately and never mixed with the orders above.
                </p>
              ) : null}
            </div>

            <p className="text-neutral-600 text-xs border-t border-white/5 pt-4">
              Orders placed before this was switched on have no source — there was nothing recorded at the time.
              That is why the announcement emails now carry the question too.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
